import {
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'services/constants/tableStructureTypes';
import { DATES, DATES_ID } from 'services/constants/dates';
import {
	VAT_CORRECTIONS_SCHEME_NAMES,
	VatCorrectionsScheme,
	VatCorrectionsStatus,
} from 'redux/reducers/vatCorrections/constants';
import { TVatCorrectionsTableItem } from './types';

const DATE_FILTER: [string, string][] = [
	[DATES_ID.TODAY, DATES.TODAY],
	[DATES_ID.YESTERDAY, DATES.YESTERDAY],
	[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
	[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
	[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
	[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
	[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
	[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
	[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
	[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
	[DATES_ID.CUSTOM_RANGE_PAST, DATES.CUSTOM_RANGE],
];

const SCHEME_FILTER: [string, string][] = [
	[VatCorrectionsScheme.NON_UNION, VAT_CORRECTIONS_SCHEME_NAMES[VatCorrectionsScheme.NON_UNION]],
	[VatCorrectionsScheme.UNION, VAT_CORRECTIONS_SCHEME_NAMES[VatCorrectionsScheme.UNION]],
	[VatCorrectionsScheme.IMPORT, VAT_CORRECTIONS_SCHEME_NAMES[VatCorrectionsScheme.IMPORT]],
	[
		VatCorrectionsScheme.NON_EU_IMPORT,
		VAT_CORRECTIONS_SCHEME_NAMES[VatCorrectionsScheme.NON_EU_IMPORT],
	],
];

const SCHEME_COLORS: [string, string][] = SCHEME_FILTER.map(([key]) => [key, 'primary.light']);

const APPROVAL_STATUS_FILTER: [string, string][] = [
	[VatCorrectionsStatus.APPROVED, 'Approved'],
	[VatCorrectionsStatus.REJECTED, 'Rejected'],
	[VatCorrectionsStatus.PENDING, 'Pending'],
];

const APPROVAL_STATUS_COLORS: [string, string][] = [
	[VatCorrectionsStatus.APPROVED, 'success.light'],
	[VatCorrectionsStatus.PENDING, 'warning.light'],
	[VatCorrectionsStatus.REJECTED, 'error.light'],
];

export const VAT_CORRECTIONS_ACTIONS = {
	DOWNLOAD: 'Download File',
} as const;

export const vatCorrectionsTableStructure: TTableStructure<
	keyof TVatCorrectionsTableItem,
	TVatCorrectionsTableItem
>[] = [
	{
		name: 'Report',
		selector: 'scheme',
		show: true,
		sortable: true,
		filterable: true,
		filter: SCHEME_FILTER,
		colors: SCHEME_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Order',
		selector: 'relatedOrder',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.OPEN_IN_NEW_TAB,
		link: (item: TVatCorrectionsTableItem) => (item?.orderId ? `/order/${item?.orderId}` : null),
		align: ETableStructureAlign.LEFT,
		tooltip: 'Open order in new tab',
	},
	{
		name: 'Country',
		selector: 'country',
		show: true,
		sortable: true,
		filterable: true,
		colSpan: 1,
		type: ETableStructureType.COUNTRY_CODE_SUPPORTED,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Requested On',
		selector: 'timestamp',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE_TIME,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Correction Period',
		selector: 'correctionPeriod',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.CENTER,
		value: (item: TVatCorrectionsTableItem) => item?.correctionPeriod ?? '',
	},
	{
		name: 'Action Date',
		selector: 'approvedRejectedOn',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.DATE_TIME,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Reporting Period',
		selector: 'reportingYear',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.CENTER,
		value: (item: TVatCorrectionsTableItem) => {
			if (!item?.reportingYear || !item?.reportingMonthQuarter) return '';
			const prefix =
				item.scheme === VatCorrectionsScheme.UNION || item.scheme === VatCorrectionsScheme.NON_UNION
					? 'Q'
					: 'M';
			return `${item.reportingYear}-${prefix}${item.reportingMonthQuarter}`;
		},
	},
	{
		name: 'Notes',
		selector: 'actionRemarks',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.TEXT,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Value',
		selector: 'requestedValueCorrection',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Status',
		selector: 'approvalStatus',
		show: true,
		sortable: true,
		filterable: true,
		filter: APPROVAL_STATUS_FILTER,
		colors: APPROVAL_STATUS_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Actions',
		selector: 'id',
		show: true,
		sortable: false,
		filterable: false,
		noName: true,
		colSpan: 1,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.LEFT,
		buttonFilter: (item: TVatCorrectionsTableItem) => {
			if (!item?.filename) return [];
			return [{ name: VAT_CORRECTIONS_ACTIONS.DOWNLOAD }];
		},
	},
];
